import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Link,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import { motion } from 'framer-motion';
import { getTranslation } from '../translations';
import { useLanguage } from '../contexts/LanguageContext';
import theme from '../theme';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const Maintenance = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const translations = {
    title: {
      en: 'PARIS SWING CLASSIC',
      fr: 'PARIS SWING CLASSIC',
      de: 'PARIS SWING CLASSIC',
      pl: 'PARIS SWING CLASSIC'
    },
    dates: {
      en: 'JANUARY 23 - 25, 2026',
      fr: '23 - 25 JANVIER 2026',
      de: '23. - 25. JANUAR 2026',
      pl: '23 - 25 STYCZNIA 2026'
    },
    comingSoon: {
      en: 'COMING SOON',
      fr: 'BIENTÔT DISPONIBLE',
      de: 'DEMNÄCHST VERFÜGBAR',
      pl: 'WKRÓTCE DOSTĘPNE'
    },
    maintenanceMessage: {
      en: 'We are currently preparing the next edition of Paris Swing Classic. Registration will open on June 25, 2026 at 7:00 PM (Paris time).',
      fr: 'Nous préparons actuellement la prochaine édition du Paris Swing Classic. Les inscriptions ouvriront le 25 juin 2026 à 19h.',
      de: 'Wir bereiten derzeit die nächste Ausgabe des Paris Swing Classic vor. Die Anmeldung öffnet am 25. Juni 2026 um 19:00 Uhr (Pariser Zeit)',
      pl: 'Obecnie przygotowujemy kolejną edycję Paris Swing Classic. Rejestracja rozpocznie się 25 czerwca 2026 o godzinie 19:00 (czasu paryskiego).'
    },
    stayTuned: {
      en: 'Stay tuned for updates and mark your calendar for an unforgettable weekend of West Coast Swing in Paris!',
      fr: 'Restez à l\'écoute pour les mises à jour et notez les dates dans votre calendrier pour un week-end inoubliable de West Coast Swing à Paris !',
      de: 'Bleiben Sie auf dem Laufenden und markieren Sie sich den Termin für ein unvergessliches Wochenende mit West Coast Swing in Paris!',
      pl: 'Śledź aktualizacje i zaznacz w kalendarzu ten niezapomniany weekend West Coast Swing w Paryżu!'
    },
    contactUs: {
      en: 'CONTACT US',
      fr: 'CONTACTEZ-NOUS',
      de: 'KONTAKTIEREN SIE UNS',
      pl: 'SKONTAKTUJ SIĘ Z NAMI'
    },
    email: {
      en: 'Email',
      fr: 'Email',
      de: 'E-Mail',
      pl: 'Email'
    },
    followUs: {
      en: 'FOLLOW US',
      fr: 'SUIVEZ-NOUS',
      de: 'FOLGEN SIE UNS',
      pl: 'OBSERWUJ NAS'
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'secondary.main',
          color: 'white',
          py: { xs: 6, sm: 8, md: 12 },
          px: { xs: 2, sm: 3, md: 4 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            {/* Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, sm: 4 } }}>
              <img 
                src="/images/logo.png" 
                alt="Paris Swing Classic Logo" 
                style={{ 
                  maxWidth: isMobile ? '160px' : isTablet ? '180px' : '220px',
                  width: '100%',
                  height: 'auto',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                }} 
              />
            </Box>
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              color="primary.main" 
              sx={{ mb: { xs: 1, sm: 2 }, textAlign: 'center' }}
            >
              {translations.comingSoon[language]}
            </Typography>
            <Typography 
              variant={isMobile ? "h3" : isTablet ? "h2" : "h1"} 
              sx={{ 
                mb: { xs: 2, sm: 3 }, 
                textAlign: 'center',
                fontSize: isMobile ? '1.75rem' : isTablet ? '2.75rem' : '3.75rem'
              }}
            >
              {translations.title[language]}
            </Typography>
            <Typography 
              variant={isMobile ? "h6" : isTablet ? "h5" : "h4"} 
              sx={{ 
                mb: { xs: 2, sm: 3, md: 4 }, 
                color: 'primary.main', 
                textAlign: 'center' 
              }}
            >
              {translations.dates[language]}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Grid container spacing={6} justifyContent="center">
          <Grid item xs={12} md={10}>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              <Paper 
                elevation={0}
                sx={{ 
                  p: { xs: 3, sm: 4, md: 6 }, 
                  textAlign: 'center',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', 
                  borderRadius: 0,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderColor: 'rgba(200, 164, 92, 0.3)',
                  mb: { xs: 3, sm: 4, md: 6 },
                  overflow: 'hidden'
                }}
              >
                <Typography 
                  variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} 
                  sx={{ 
                    mb: { xs: 2, sm: 3, md: 4 },
                    fontSize: isMobile ? '1.25rem' : isTablet ? '1.75rem' : '2.5rem',
                    lineHeight: 1.4
                  }}
                >
                  {translations.maintenanceMessage[language]}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: { xs: 2, sm: 3, md: 4 }, 
                    fontSize: isMobile ? '1rem' : isTablet ? '1.1rem' : '1.2rem',
                    px: { xs: 1, sm: 2, md: 4 }
                  }}
                >
                  {translations.stayTuned[language]}
                </Typography>
                
                <Box sx={{ mt: { xs: 3, sm: 4, md: 6 }, mb: 2, color: theme.palette.text.primary }}>
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ mb: { xs: 2, sm: 3 } }}
                  >
                    {translations.contactUs[language]}
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    mb: 2,
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 1 : 0
                  }}>
                    <EmailIcon color="primary" sx={{ mr: isMobile ? 0 : 1 }} />
                    <Link 
                      href="mailto:contact@greenfrip.com" 
                      color="primary" 
                      underline="hover"
                      sx={{ 
                        wordBreak: 'break-word',
                        fontSize: isMobile ? '0.9rem' : '1rem'
                      }}
                    >
                      contact@greenfrip.com
                    </Link>
                  </Box>
                  
                  <Divider sx={{ my: { xs: 2, sm: 3 }, width: { xs: '80%', sm: '60%', md: '50%' }, mx: 'auto' }} />
                  
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ mb: { xs: 2, sm: 3 } }}
                  >
                    {translations.followUs[language]}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Link href="https://instagram.com/parisswingclassic" target="_blank" color="primary">
                      <InstagramIcon fontSize="large" />
                    </Link>
                    <Link href="https://facebook.com/parisswingclassic" target="_blank" color="primary">
                      <FacebookIcon fontSize="large" />
                    </Link>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Maintenance;
